import { useEffect, useState, useRef } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { Chip } from "@scripts/react/components/Chip";
import type { RapidDataType } from "@/store/slices/rapidDataSlice";
import { projectSelector } from "@rflocal/shared";

interface ModelCardProps {
    model: RapidDataType;
    onModelClick: (model: RapidDataType) => void;
    formatTimestamp: (date: Date | undefined) => string;
}

export const ModelCard = ({ model, onModelClick, formatTimestamp }: ModelCardProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const observerRef = useRef<HTMLDivElement>(null);

    // Lazy load images using Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "100px" }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Use the first dataset for display purposes
    const primaryDataset = model.datasets?.[0];

    const getClasses = (): string[] => {
        // Try to get classes from dataset first
        const datasetClasses = (primaryDataset as any)?.classes;
        if (datasetClasses) {
            // Handle both array and object formats
            if (Array.isArray(datasetClasses)) {
                return datasetClasses;
            }
            if (typeof datasetClasses === "object") {
                return Object.keys(datasetClasses);
            }
        }

        // Try to get classes from models object
        if (model.models) {
            const modelEntries = Object.values(model.models).flat();
            for (const modelEntry of modelEntries) {
                if (modelEntry?.classes) {
                    if (Array.isArray(modelEntry.classes)) {
                        return modelEntry.classes;
                    }
                    if (typeof modelEntry.classes === "object") {
                        return Object.keys(modelEntry.classes);
                    }
                }
            }
        }

        return [];
    };

    const classes = getClasses();

    const getBackgroundImage = () => {
        if (!primaryDataset?.icon) {
            return undefined;
        }

        const { icon, iconOwner, iconHasAnnotation, annotation } = primaryDataset;
        const baseUrl = `https://${projectSelector.cdnHost()}/${iconOwner}/${icon}`;

        if (iconHasAnnotation && annotation) {
            return `url(${baseUrl}/annotation-${annotation}.png), url(${baseUrl}/original.jpg)`;
        }

        return `url(${baseUrl}/original.jpg)`;
    };

    return (
        <div
            ref={observerRef}
            onClick={() => onModelClick(model)}
            className={twClassNames(
                "group flex cursor-pointer flex-col bg-white p-0.5 transition-all"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Thumbnail */}
            <div
                className={twClassNames(
                    "flex overflow-hidden rounded-lg bg-white p-0.5 ring-1 transition duration-200",
                    isHovered ? "shadow-md ring-2 ring-brand" : "ring-gray-300"
                )}
                style={{ aspectRatio: 16 / 9 }}
            >
                {isVisible && primaryDataset?.icon ? (
                    <div
                        className="h-full w-full rounded-md bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: getBackgroundImage() }}
                    />
                ) : (
                    <div
                        className={twClassNames(
                            "flex h-full w-full items-center justify-center rounded-md transition duration-200",
                            isHovered ? "bg-tint text-purboflow-500" : "text-border bg-gray-100"
                        )}
                    >
                        <i className="far fa-image text-3xl" />
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-col gap-0.5 px-3 py-2">
                {/* Model name */}
                <h3
                    className={twClassNames(
                        "truncate font-semibold",
                        isHovered ? "text-brand" : "text-primary"
                    )}
                >
                    {model.workflowName || "Untitled Model"}
                </h3>

                {/* Creation timestamp */}
                {primaryDataset?.createdAt && (
                    <p
                        className={twClassNames(
                            "text-xs",
                            isHovered ? "text-brand" : "text-tertiary"
                        )}
                    >
                        {formatTimestamp(
                            typeof primaryDataset.createdAt === "string"
                                ? new Date(primaryDataset.createdAt)
                                : primaryDataset.createdAt
                        )}
                    </p>
                )}
            </div>
        </div>
    );
};
